import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { PreviewCtaView, PreviewMediaView, PreviewSlideView } from './preview-model';

export type PreviewDevice = 'desktop' | 'phone';

function MediaPreview({ media, device }: { media: PreviewMediaView; device: PreviewDevice }) {
  const { t } = useTranslation('content');
  const frame = cn('w-full', device === 'phone' ? 'aspect-[4/5]' : 'aspect-[16/9]');
  switch (media.state) {
    case 'none':
      return null;
    case 'pending':
      return (
        <Skeleton
          className={cn(frame, 'rounded-none')}
          aria-label={t('blockPreview.media.loading')}
        />
      );
    case 'missing':
      return (
        <div
          className={cn(
            frame,
            'bg-muted text-muted-foreground flex flex-col items-center justify-center gap-1 text-xs',
          )}
        >
          <ImageOff className="size-5 text-amber-600" aria-hidden />
          {t('blockPreview.media.missing')}
        </div>
      );
    case 'ready':
      return media.kind === 'video' ? (
        <video
          src={media.url}
          className={cn(frame, 'bg-muted object-cover')}
          muted
          playsInline
          preload="metadata"
          aria-label={media.alt || t('blockPreview.media.video')}
        />
      ) : (
        <img src={media.url} alt={media.alt} className={cn(frame, 'bg-muted object-cover')} />
      );
  }
}

function CtaPreview({ cta, primary }: { cta: PreviewCtaView; primary: boolean }) {
  const { t } = useTranslation('content');
  const broken = cta.state === 'broken';
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center rounded-md px-2.5 text-xs font-medium',
        primary ? 'bg-primary text-primary-foreground' : 'border',
        broken && 'bg-muted text-muted-foreground border-dashed line-through',
      )}
      title={broken ? t('blockPreview.cta.broken') : (cta.href ?? undefined)}
      data-state={cta.state}
    >
      {cta.label}
      {broken ? <span className="sr-only"> ({t('blockPreview.cta.broken')})</span> : null}
    </span>
  );
}

export interface SlidePreviewProps {
  slide: PreviewSlideView;
  device: PreviewDevice;
}

/** One slide, or a rail header: media on top, copy and buttons under it. */
export function SlidePreview({ slide, device }: SlidePreviewProps) {
  const { t } = useTranslation('content');
  // Phones get the mobile media when the slide has one.
  const media =
    device === 'phone' && slide.mobileMedia.state !== 'none' ? slide.mobileMedia : slide.media;
  const hasCopy = !!(slide.eyebrow || slide.title || slide.subtitle || slide.description);
  const empty = media.state === 'none' && !hasCopy && slide.ctas.length === 0;

  return (
    <div className="bg-background overflow-hidden rounded-md border">
      <MediaPreview media={media} device={device} />
      {empty ? (
        <p className="text-muted-foreground p-4 text-center text-xs">
          {t('blockPreview.slide.empty')}
        </p>
      ) : hasCopy || slide.ctas.length > 0 ? (
        <div className="flex flex-col gap-1 p-3">
          {slide.eyebrow ? (
            <p className="text-muted-foreground text-[0.65rem] font-medium tracking-wider uppercase">
              {slide.eyebrow}
            </p>
          ) : null}
          {slide.title ? (
            <p className="text-base leading-tight font-semibold">{slide.title}</p>
          ) : null}
          {slide.subtitle ? <p className="text-sm">{slide.subtitle}</p> : null}
          {slide.description ? (
            <p className="text-muted-foreground line-clamp-3 text-xs">{slide.description}</p>
          ) : null}
          {slide.ctas.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {slide.ctas.map((cta, i) => (
                <CtaPreview key={i} cta={cta} primary={i === 0} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export interface BannerPreviewProps {
  slides: PreviewSlideView[];
  /** The slide on screen; kept in sync with the expanded card in the form. */
  index: number;
  onIndexChange: (index: number) => void;
  device: PreviewDevice;
}

/** A banner as a slider: the current slide, arrows and one dot per slide. */
export function BannerPreview({ slides, index, onIndexChange, device }: BannerPreviewProps) {
  const { t } = useTranslation('content');
  const at = Math.max(0, Math.min(index, slides.length - 1));
  const current = slides[at];
  if (!current) return null;

  return (
    <div className="flex flex-col gap-2">
      <SlidePreview slide={current} device={device} />
      {slides.length > 1 ? (
        <div className="flex items-center justify-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={at === 0}
            onClick={() => onIndexChange(at - 1)}
            aria-label={t('blockPreview.slide.previous')}
          >
            <ChevronLeft aria-hidden />
          </Button>
          <div className="flex items-center">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                className="group focus-visible:ring-ring/50 rounded-full p-1 focus-visible:ring-[3px] focus-visible:outline-none"
                aria-label={t('blockPreview.slide.show', { index: i + 1 })}
                aria-current={i === at ? 'true' : undefined}
                onClick={() => onIndexChange(i)}
              >
                <span
                  className={cn(
                    'block size-2 rounded-full',
                    i === at
                      ? 'bg-primary'
                      : 'bg-muted-foreground/30 group-hover:bg-muted-foreground/60',
                  )}
                />
              </button>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={at === slides.length - 1}
            onClick={() => onIndexChange(at + 1)}
            aria-label={t('blockPreview.slide.next')}
          >
            <ChevronRight aria-hidden />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
